import { BaseClient, normalizeQuery } from '../base-client';
import type {
  ApplyTo,
  EditImpactResponse,
  EntityVersionAmendResponse,
  EntityVersionsResponse,
  PatchMeta,
} from '../entity-versions/types';
import type { Skill } from '../skills/types';
import type {
  AddSkillInput,
  BulkAddSkillsInput,
  ChangeEmailInput,
  CreateUserInput,
  ExportUsersQuery,
  ImportUsersResult,
  ListSrControllerCandidatesQuery,
  ListUsersQuery,
  MeRelated,
  MyRatedSkill,
  PatchUserInput,
  RateSkillInput,
  User,
  UserWithLabels,
} from './types';

type One = { data: User };
/** Detail read carries scope-derived flags + relationship labels:
 *  - `costVisible` (may the caller see this employee's cost), `editable` (may
 *    the caller edit) — drive the cost lock + edit affordances.
 *  - `addressVisible` (may the caller see this employee's PII/address) — drives
 *    the address lock. When false the address fields on `data` are redacted
 *    (nulled) server-side, so don't infer visibility from the values.
 *  - `managerName` / `srControllerName` — resolved server-side so the detail
 *    page never fetches those (scope-gated) records just to show a name.
 *  - `customerTeamCount` — total memberships; gates the customer-team section. */
type DetailRelated = {
  costVisible: boolean;
  editable: boolean;
  addressVisible: boolean;
  managerName: string | null;
  srControllerName: string | null;
  customerTeamCount: number;
};
type DetailResponse = { data: User; related: DetailRelated };
type MeResponse = { data: User; related: MeRelated };
type Skills = { data: Skill[] };
type Meta = { total: number; limit: number; offset: number };
type ListResponse = { data: User[]; meta: Meta };
type ListWithLabelsResponse = { data: UserWithLabels[]; meta: Meta };
type ImportResponse = { data: ImportUsersResult };

export class UsersClient extends BaseClient {
  /** The list page — rows enriched with system/service-role, department, and
   *  manager names (resolved server-side). */
  list(query: ListUsersQuery = {}): Promise<ListWithLabelsResponse> {
    return this.request('/api/users', { query: normalizeQuery(query) });
  }

  /** Batch fetch users by exactly the referenced ids — bounded, never
   *  capped. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<User>('/api/users/find', ids);
  }

  /**
   * Candidates for the SR Controller picker: active users already acting as
   * one, plus `includeId` (the employee being edited, so they can always
   * find and pick themselves — a Sr. Controller's own hours have nobody
   * above them to assign).
   */
  listSrControllerCandidates(query: ListSrControllerCandidatesQuery = {}): Promise<ListResponse> {
    return this.request('/api/users/sr-controllers', { query: normalizeQuery(query) });
  }

  me(): Promise<MeResponse> {
    return this.request('/api/users/me');
  }

  /** Self-edit. The server only honors a whitelisted field set on this
   *  endpoint (today: `timezone` only) — anything else is silently dropped
   *  by Zod parsing. */
  patchMe(input: { timezone?: string }): Promise<One> {
    return this.request('/api/users/me', { method: 'PATCH', body: input });
  }

  /**
   * Users the caller can submit timesheets on behalf of during
   * `[fromDate, toDate]`. Result includes self. Used by the timesheet
   * page to render the "viewing for" picker.
   */
  submittableByMe(params: { fromDate: string; toDate: string }): Promise<{ data: User[] }> {
    return this.request('/api/users/submittable-by-me', { query: params });
  }

  get(id: string): Promise<DetailResponse> {
    return this.request(`/api/users/${encodeURIComponent(id)}`);
  }

  /** Active direct reports of `id` (users this employee manages). Returned as
   *  a tenant-scoped relationship list — names only, not access-scoped. */
  listDirectReports(id: string): Promise<{ data: User[] }> {
    return this.request(`/api/users/${encodeURIComponent(id)}/direct-reports`);
  }

  create(input: CreateUserInput): Promise<One> {
    return this.request('/api/users', { method: 'POST', body: input });
  }

  /** `applyTo` is the answer to "When did this become true?" — omitted,
   *  the edit applies from today (see {@link ApplyTo}). */
  patch(id: string, input: PatchUserInput, applyTo?: ApplyTo): Promise<One & { meta?: PatchMeta }> {
    return this.request(`/api/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
      query: { applyTo },
    });
  }

  /** What this edit would change and how many logged timesheet entries the
   *  "always true" answer would restate. Writes nothing. */
  previewPatch(id: string, input: PatchUserInput): Promise<EditImpactResponse> {
    return this.request(`/api/users/${encodeURIComponent(id)}/edit-preview`, {
      method: 'POST',
      body: input,
    });
  }

  /**
   * Change the user's email — the login identity. Separate from `patch`
   * because the server updates `ba_user.email` alongside `users.email` on
   * this endpoint only. Same `user.edit` gate + edit scope as `patch`.
   */
  changeEmail(id: string, input: ChangeEmailInput): Promise<One> {
    return this.request(`/api/users/${encodeURIComponent(id)}/email`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/users/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/users/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }

  listSkills(id: string): Promise<Skills> {
    return this.request(`/api/users/${encodeURIComponent(id)}/skills`);
  }

  /** This employee's record history, newest effective date first. Address
   *  changes come back with `restricted: true` and no values for callers
   *  without `user.pii.view`. */
  listVersions(id: string): Promise<EntityVersionsResponse> {
    return this.request(`/api/users/${encodeURIComponent(id)}/versions`);
  }

  /**
   * Amend what a PAST version of this record says, in place (D10).
   *
   * No date is sent: effective dates are never rewritten, so the version's
   * range is not editable and nothing splits. The body is an ordinary patch —
   * the server keeps the versioned columns and drops the rest.
   *
   * Returns the later versions still carrying each replaced value. An amend
   * touches ONE version by decision, so this is what the UI needs to tell the
   * operator what their change did not reach.
   */
  amendVersion(
    id: string,
    version: number,
    input: PatchUserInput,
  ): Promise<EntityVersionAmendResponse> {
    return this.request(`/api/users/${encodeURIComponent(id)}/versions/${version}`, {
      method: 'PATCH',
      body: input,
    });
  }

  addSkill(id: string, input: AddSkillInput): Promise<Skills> {
    return this.request(`/api/users/${encodeURIComponent(id)}/skills`, {
      method: 'POST',
      body: input,
    });
  }

  bulkAddSkills(id: string, input: BulkAddSkillsInput): Promise<Skills> {
    return this.request(`/api/users/${encodeURIComponent(id)}/skills/bulk-add`, {
      method: 'POST',
      body: input,
    });
  }

  removeSkill(id: string, skillId: string): Promise<Skills> {
    return this.request(
      `/api/users/${encodeURIComponent(id)}/skills/${encodeURIComponent(skillId)}`,
      { method: 'DELETE' },
    );
  }

  /** "My Skills" — the full active catalog with the caller's own rating
   *  overlaid. Always self; there is no userId param. */
  myRatedCatalog(): Promise<{ data: MyRatedSkill[] }> {
    return this.request('/api/users/me/skills/ratings');
  }

  /** Set or change the caller's own rating for one catalog entry. */
  rateSkill(skillId: string, input: RateSkillInput): Promise<{ data: MyRatedSkill }> {
    return this.request(`/api/users/me/skills/${encodeURIComponent(skillId)}/rating`, {
      method: 'PATCH',
      body: input,
    });
  }

  /**
   * `X-Impersonate-Confirm: 1` is a CSRF defense — the custom header forces
   * CORS preflight so a form-style cross-origin POST can't reach this even
   * if BetterAuth's cookie policy ever drifts away from SameSite=Lax.
   */
  startImpersonation(userId: string): Promise<{ data: { impersonating: true; userId: string } }> {
    return this.request(`/api/impersonate/${encodeURIComponent(userId)}`, {
      method: 'POST',
      body: {},
      headers: { 'x-impersonate-confirm': '1' },
    });
  }

  stopImpersonation(): Promise<{ data: { impersonating: false } }> {
    return this.request('/api/impersonate/stop', {
      method: 'POST',
      body: {},
      headers: { 'x-impersonate-confirm': '1' },
    });
  }

  /**
   * Download users as an XLSX blob. `filter` mirrors the list-page filters
   * (status, role, manager, custom fields, search text, sort); pass `{}` to
   * export every employee.
   */
  exportXlsx(filter: ExportUsersQuery = {}): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/users/export', { query: normalizeQuery(filter) });
  }

  /** Download a blank XLSX template (header row only). */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/users/import/template');
  }

  /**
   * Upload an .xlsx or .csv. Server validates every row before
   * persisting any of them; failed rows come back in `result.rows`
   * with per-row error messages for the FE report.
   */
  importFile(file: File): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.requestMultipart('/api/users/import', fd);
  }
}
